import type {
  Group,
  Option,
} from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import type { IntlService } from 'ember-intl';
import type ErrorTrackingService from 'frontend-utils/services/error-tracking';
import type MappingCategoryBenchmark from 'lumen/utils/datamodels/mapping-category-benchmark';

// eslint-disable-next-line max-params
export function createGroupedCategories<
  MappingCategory extends MappingCategoryBenchmark,
>(
  rootCategory: MappingCategory,
  shouldAddCategoryAsOption: boolean,
  intl: IntlService,
  errorTracking: ErrorTrackingService,
  isSelectingGroupEnabled: boolean,
  disableNestedOptions?: boolean,
): (Option | Group<Option>)[] {
  const options: (Option | Group<Option>)[] = [];

  rootCategory.childrenSorted.forEach((category) => {
    const categoryName = category.getDisplayName(intl, errorTracking);

    if (categoryName) {
      const disabled = disableNestedOptions && category.depth > 0;

      const categoryAsOption: Option = {
        label: categoryName,
        id: category.id,
        disabled,
      };

      // If we disable nested options, don't add any nested navigable options, because those can't be disabled.
      // We can either disable every navigable option in the entire tree, or none at all.
      // This is because of how to CollapsibleGroup is implemented in the ComboSelectInput in Prism:
      // the component doesn't have any knowledge of the option.
      if (category.children.length && !disabled) {
        const nestedOptions = createGroupedCategories(
          category,
          shouldAddCategoryAsOption,
          intl,
          errorTracking,
          isSelectingGroupEnabled,
          disableNestedOptions,
        );

        // Add the category itself (the group name)to its options because we want it to be selectable as well.
        if (shouldAddCategoryAsOption) {
          nestedOptions.unshift(categoryAsOption);
        }

        options.push({
          groupName: categoryName,
          label: categoryName, // Add value that is used to display the name in the dropdown trigger, in case the option group itself is selectable.
          id: category.id,
          options: nestedOptions,
          unsafe_isCollapsed: true,
          unsafe_isSelectable: isSelectingGroupEnabled,
          disabled,
        });
      } else {
        options.push(categoryAsOption);
      }
    }
  });

  return options;
}

export function findOptionById(options: Option[], id: string): Option | null {
  let foundOption = null;

  // Using for loop instead of forEach so we can return the found option early.
  for (const option of options) {
    const { id: optionId, options: childOptions } = option;

    if (optionId === id) {
      foundOption = option;
    } else if (childOptions) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      foundOption = findOptionById(childOptions, id);
    }

    if (foundOption) {
      return foundOption;
    }
  }

  return foundOption;
}
